import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { isEmpty, uniq } from 'lodash';
import React, { Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TranslationNamespace } from 'i18n';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
  title?: string;
  inputLabel?: string;
  separatorLabel?: string;
  parseButtonLabel?: string;
  confirmMessage?: (count: number) => string;
  noIdsMessage?: string;
};

export const ImportListDialog: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  title,
  inputLabel,
  separatorLabel,
  parseButtonLabel,
  confirmMessage,
  noIdsMessage,
}) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.import_list_dialog',
  });

  const [separator, setSeparator] = useState(',');
  const [rawInput, setRawInput] = useState('');
  const [parsedIds, setParsedIds] = useState<string[] | null>(null);

  const handleParse = useCallback(() => {
    const sep = separator || ',';
    const ids = uniq(
      rawInput
        .split(sep)
        .map((v) => v.trim())
        .filter((v) => !isEmpty(v)),
    );
    setParsedIds(ids);
  }, [separator, rawInput]);

  const handleConfirm = useCallback(() => {
    if (!isEmpty(parsedIds)) {
      onConfirm(parsedIds!);
    }
    setRawInput('');
    setSeparator(',');
    setParsedIds(null);
  }, [parsedIds, onConfirm]);

  const handleClose = useCallback(() => {
    setRawInput('');
    setSeparator(',');
    setParsedIds(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title ?? t('title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={separatorLabel ?? t('separator_label')}
            value={separator}
            onChange={(e) => {
              setSeparator(e.target.value);
              setParsedIds(null);
            }}
            size="small"
            sx={{ width: 120 }}
          />
          <TextField
            label={inputLabel ?? t('input_label')}
            value={rawInput}
            onChange={(e) => {
              setRawInput(e.target.value);
              setParsedIds(null);
            }}
            multiline
            minRows={4}
            maxRows={5}
            fullWidth
          />
          {parsedIds !== null && (
            <Fragment>
              <Alert severity={!isEmpty(parsedIds) ? 'info' : 'warning'}>
                {!isEmpty(parsedIds)
                  ? (
                      confirmMessage ??
                      ((count) => t('confirm_message', { count }))
                    )(parsedIds.length)
                  : noIdsMessage ?? t('no_ids_found')}
              </Alert>
              {!isEmpty(parsedIds) && (
                <Button variant="outlined" onClick={handleConfirm}>
                  {tCommon('buttons.confirm')}
                </Button>
              )}
            </Fragment>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{tCommon('buttons.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleParse}
          disabled={!rawInput.trim()}
        >
          {parseButtonLabel ?? t('parse_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
